import { useContext, useState, useCallback } from 'react';

import RadioContext from '../../RadioContext';
import Modal from '../Modal';
import ModalContent from '../ModalContent';
import ModalActions from '../ModalActions';
import Button from '../Button';
import Snackbar from '../Snackbar';
import Alert from '../Alert';

import * as apiClient from '../../../apiClient';
import { parseRequestError } from '../../../utils/errors';

export function usePresetMenu() {
  const { reloadSettings } = useContext(RadioContext);

  const [presets, setPresets] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingPreset, setPendingPreset] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);

  const ensureLoaded = useCallback(async () => {
    if (loaded || loading) {
      return;
    }

    setLoading(true);
    try {
      const items = await apiClient.getPresets();
      setPresets(items || []);
      setLoaded(true);
    } catch {
      // silently ignore — preset selector is non-critical
    } finally {
      setLoading(false);
    }
  }, [loaded, loading]);

  const handleSelectPreset = (preset) => () => {
    setPendingPreset(preset);
  };

  const handleCancelApplyPreset = () => {
    setPendingPreset(null);
  };

  const handleConfirmApplyPreset = (onApplied) => async () => {
    const preset = pendingPreset;
    setPendingPreset(null);
    try {
      await apiClient.applyPreset(preset.id);
      await reloadSettings();
      setLoaded(false);
      setActionMessage({ type: 'success', message: 'Preset aplicado com sucesso.' });
      if (onApplied) {
        onApplied();
      }
    } catch (error) {
      const [errorMessage] = parseRequestError(error);
      setActionMessage({ type: 'error', message: errorMessage });
    }
  };

  const items = presets.map((preset) => ({
    id: `preset-${preset.id}`,
    title: preset.name,
    onClick: handleSelectPreset(preset),
  }));

  return {
    items,
    loading,
    ensureLoaded,
    pendingPreset,
    handleCancelApplyPreset,
    handleConfirmApplyPreset,
    actionMessage,
    setActionMessage,
  };
}

export function PresetMenuDialogs({ presetMenu, onApplied }) {
  const {
    pendingPreset,
    handleCancelApplyPreset,
    handleConfirmApplyPreset,
    actionMessage,
    setActionMessage,
  } = presetMenu;

  return (
    <>
      {actionMessage && (
        <Snackbar open onClose={() => setActionMessage(null)}>
          <Alert type={actionMessage.type} className="shadow-md">
            {actionMessage.message}
          </Alert>
        </Snackbar>
      )}
      <Modal open={Boolean(pendingPreset)} onClose={handleCancelApplyPreset}>
        <ModalContent>
          <div>
            <h2 className="mb-6 text-2xl font-bold">Aplicar preset</h2>
            <h3>
              Tem certeza que deseja aplicar o preset
              {pendingPreset ? ` "${pendingPreset.name}"` : ''} nesta rádio?
            </h3>
          </div>
        </ModalContent>
        <ModalActions>
          <Button onClick={handleCancelApplyPreset}>Cancelar</Button>
          <Button
            color="primary"
            onClick={handleConfirmApplyPreset(onApplied)}
          >
            Ok
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
}
